/**
 * strategyHorizonFlags.ts — the horizon rule, applied to the plans this module actually builds.
 *
 * The RULE lives in shared/src/calculation/strategyHorizon.ts and knows only a person and a need.
 * This file knows the plans: which engine candidate carries an annuity and with what residual, which
 * one sells the home to buy a smaller one, who owns the annuity, and who would live in the new home.
 * Every surface — the engine's cards and leader picking, Manual Control, the advice document — reads
 * its warning from here, so none of them can describe the same plan differently (owner, 23 Sep 2026).
 *
 * It moves no figure. It reads a plan and returns words, or null.
 */
import {
    horizonBandForPerson,
    horizonNeededForHomeSale,
    horizonNeededForLifetimeAnnuity,
    horizonWarning,
    type HorizonBand,
    type HorizonNeed,
} from '../../../../shared/src/calculation/strategyHorizon.ts';
import { calculateAge } from '../../utils/index.ts';

/** The fact-find fields this file reads; a structural subset so callers need not cast. */
export interface HorizonFactFind {
    adviceFor?: string;
    client?: { firstName?: string; dob?: string; healthStatus?: unknown; sex?: unknown };
    partner?: { firstName?: string; dob?: string; healthStatus?: unknown; sex?: unknown };
}

export interface PersonHorizon {
    name: string;
    band: HorizonBand | null;
    /** Whether a recorded "poor" health answer shortened the band. */
    healthConsidered: boolean;
}

export interface HorizonFlag {
    warning: string;
    need: HorizonNeed;
    /** Which person's horizon was judged. */
    person: 'client' | 'partner' | 'joint';
    /** What was flagged. */
    strategy: 'annuity' | 'downsize';
}

const health = (p: HorizonFactFind['client']) => String(p?.healthStatus ?? '').trim().toLowerCase();

/** Each person's horizon, from their age and recorded health. */
export function personHorizons(factFind: HorizonFactFind): { client: PersonHorizon; partner: PersonHorizon | null; joint: PersonHorizon | null } {
    const clientAge = calculateAge(factFind.client?.dob);
    const partnerAge = factFind.adviceFor === 'couple' ? calculateAge(factFind.partner?.dob) : null;
    const clientName = (factFind.client?.firstName || '').trim() || 'the client';
    const partnerName = (factFind.partner?.firstName || '').trim() || 'the partner';
    const client: PersonHorizon = {
        name: clientName,
        band: horizonBandForPerson({ age: clientAge, healthStatus: factFind.client?.healthStatus, sex: factFind.client?.sex }),
        healthConsidered: health(factFind.client) === 'poor',
    };
    const partner: PersonHorizon | null = partnerAge == null ? null : {
        name: partnerName,
        band: horizonBandForPerson({ age: partnerAge, healthStatus: factFind.partner?.healthStatus, sex: factFind.partner?.sex }),
        healthConsidered: health(factFind.partner) === 'poor',
    };
    // A joint annuity is planned on the joint expectancy; a "poor" answer from either shortens it.
    const joint: PersonHorizon | null = partnerAge == null || clientAge == null ? null : {
        name: `${clientName} and ${partnerName}`,
        band: horizonBandForPerson({
            age: clientAge,
            partnerAge,
            sex: factFind.client?.sex,
            partnerSex: factFind.partner?.sex,
            healthStatus: health(factFind.client) === 'poor' || health(factFind.partner) === 'poor' ? 'poor' : undefined,
        }),
        healthConsidered: health(factFind.client) === 'poor' || health(factFind.partner) === 'poor',
    };
    return { client, partner, joint };
}

const annuityLabel = (residualCapital: unknown, guaranteed: boolean, indexed = false) =>
    guaranteed
        ? 'a lifetime annuity with the full amount guaranteed'
        : horizonNeededForLifetimeAnnuity(residualCapital) === 'long'
          ? (indexed ? 'a lifetime annuity rising with inflation' : 'a lifetime annuity with nil residual')
          : 'a lifetime annuity with capital returned';

/**
 * The annuity's flag, for whoever owns it. `owner` is 'client' | 'partner' | 'joint' as Manual
 * Control records it; the engine's candidates are always the client's ('ownership: client').
 */
export function annuityHorizonFlag(input: { factFind: HorizonFactFind; residualCapital: unknown; owner?: unknown; annuityType?: unknown; indexation?: unknown }): HorizonFlag | null {
    const horizons = personHorizons(input.factFind);
    const owner = input.owner === 'partner' || input.owner === 'joint' ? input.owner : 'client';
    const who = owner === 'joint' ? horizons.joint ?? horizons.client : owner === 'partner' ? horizons.partner ?? horizons.client : horizons.client;
    const need = horizonNeededForLifetimeAnnuity(input.residualCapital);
    // The guaranteed shape (annuity plus life policy) returns the whole price on death; its warning
    // is about what there is to gain, not whether it suits (9 Oct 2026).
    const guaranteed = input.annuityType === 'guaranteed';
    const warning = horizonWarning({ need, band: who.band, personName: who.name, strategyLabel: annuityLabel(input.residualCapital, guaranteed, input.indexation === true), healthConsidered: who.healthConsidered, ...(guaranteed ? { shape: 'capital_guaranteed' as const } : {}) });
    return warning ? { warning, need, person: owner, strategy: 'annuity' } : null;
}

/**
 * The home sale's flag. Only a sale that buys a smaller home is judged, and it is judged on the
 * person who would live there: the partner, for a couple, since in this module the client is in care.
 */
export function homeSaleHorizonFlag(input: { factFind: HorizonFactFind; sellsHome: boolean; newHomePrice: unknown }): HorizonFlag | null {
    if (!input.sellsHome) return null;
    const buysNewHome = (typeof input.newHomePrice === 'number' ? input.newHomePrice : parseFloat(String(input.newHomePrice ?? '0'))) > 0;
    const need = horizonNeededForHomeSale({ buysNewHome });
    if (need === 'none') return null;
    const horizons = personHorizons(input.factFind);
    const who = input.factFind.adviceFor === 'couple' && horizons.partner ? horizons.partner : horizons.client;
    const person: HorizonFlag['person'] = who === horizons.partner ? 'partner' : 'client';
    const warning = horizonWarning({ need, band: who.band, personName: who.name, strategyLabel: 'selling the home to buy a smaller one', healthConsidered: who.healthConsidered });
    return warning ? { warning, need, person, strategy: 'downsize' } : null;
}

/**
 * The shape of an engine row this file reads — loose on purpose, because the panel types these two
 * bags as `Record<string, unknown>` and the advice document as its own scenario shape.
 */
export interface HorizonEngineStrategy {
    meansTestStrategy?: Record<string, unknown> | null;
    strategyOptions?: Record<string, unknown> | null;
}

const rec = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);

/**
 * Every flag an engine candidate raises, annuity first. Most candidates raise none: the deposit at
 * any percentage, renting the home out, funeral bonds, gifts and clearing debt need no horizon.
 */
export function engineStrategyHorizonFlags(strategy: HorizonEngineStrategy, factFind: HorizonFactFind): HorizonFlag[] {
    const flags: HorizonFlag[] = [];
    const lever = rec(strategy.meansTestStrategy);
    if (lever && lever.type === 'annuity' && (Number(lever.purchaseAmount) || 0) > 0) {
        const f = annuityHorizonFlag({ factFind, residualCapital: lever.residualCapital, owner: lever.ownership, annuityType: lever.annuityType, indexation: lever.indexation });
        if (f) flags.push(f);
    }
    const opts = rec(strategy.strategyOptions);
    if (opts?.downsizeHome) {
        const details = rec(opts.downsizeDetails);
        const f = homeSaleHorizonFlag({ factFind, sellsHome: true, newHomePrice: details?.newHomePurchasePrice });
        if (f) flags.push(f);
    }
    return flags;
}

/** The one warning a card shows: the first, since the annuity's is the more consequential. */
export function engineStrategyHorizonWarning(strategy: HorizonEngineStrategy, factFind: HorizonFactFind): string | null {
    return engineStrategyHorizonFlags(strategy, factFind)[0]?.warning ?? null;
}

/**
 * A leader that was set aside: a flagged strategy that would have won a ranking. Recorded so the
 * screen and the advice document can say so, and let the reader open it — never hidden, never
 * promoted (owner, 23 Sep 2026: "I definitely don't think we should promote it as the best result").
 */
export interface HorizonSetAside<S> {
    label: string;
    strategy: S;
    warning: string;
    /** What it would have beaten the chosen leader on. */
    metric: 'legacy' | 'cashflow' | 'pension';
}
